import {
  queryOptions,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';

import toast from 'react-hot-toast';
import { Toast } from 'src/components';
import { cloudEnterprisePolicyApi } from '../clients/cloudEnterprisePolicyApiClient';
import {
  getPolicyProfile,
  sendCommandRemoteWipe,
  sendCommandSetAutolockTimeout,
  sendCommandSetPasscodeLength,
  sendCommandSoftwareAutoInstall,
  sendCommandSoftwareForceRestart,
  updatePolicyProfile,
} from '../endpoints/cloudEnterprisePolicyApi';
import { queryFleetMembers } from './fleet';
import { queryKeys } from './queryKeys';

export const queryPolicyProfiles = () =>
  cloudEnterprisePolicyApi.queryOptions('get', '/policy/v1/profiles');

export const usePolicyProfiles = () => useSuspenseQuery(queryPolicyProfiles());

export const useDefaultPolicyProfile = () =>
  useSuspenseQuery({
    ...queryPolicyProfiles(),
    select(data) {
      return data?.find((profile) => profile.isDefault) ?? data?.[0] ?? null;
    },
  });

export const queryPolicyProfile = (vars: { id: string }) =>
  queryOptions({
    queryKey: queryKeys.enterprisePolicies.profile(vars),
    queryFn: () => getPolicyProfile(vars),
  });

export const usePolicyProfile = (vars: { id: string }) =>
  useSuspenseQuery(queryPolicyProfile(vars));

export const useCreatePolicyProfile = () =>
  cloudEnterprisePolicyApi.useMutation('post', '/policy/v1/profiles', {
    onSuccess: () => {
      toast.custom(({ visible }) => (
        <Toast
          variant="success"
          title="Policy settings updated"
          className={visible ? 'animate-fade-in-down' : 'animate-fade-out-up'}
        ></Toast>
      ));
    },
  });

export const useUpdatePolicyProfile = () =>
  useMutation({
    mutationKey: queryKeys.enterprisePolicies.updateProfile,
    mutationFn: updatePolicyProfile,
    onSuccess: () => {
      toast.custom(({ visible }) => (
        <Toast
          variant="success"
          title="Policy settings updated"
          className={visible ? 'animate-fade-in-down' : 'animate-fade-out-up'}
        ></Toast>
      ));
    },
  });

/* Not yet available. */
// export const useDeletePolicyProfile = () =>
//   useMutation({
//     mutationKey: queryKeys.enterprisePolicies.deleteProfile,
//     mutationFn: deletePolicyProfile,
//   });

export const useSendCommandRemoteWipe = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.enterprisePolicies.commandRemoteWipe,
    mutationFn: sendCommandRemoteWipe,
    onSuccess: () => {
      void queryClient.invalidateQueries(queryFleetMembers());
    },
  });
};

export const useSendCommandSetPasscodeLength = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.enterprisePolicies.commandSetPasscodeLength,
    mutationFn: sendCommandSetPasscodeLength,
    onSuccess: () => {
      void queryClient.invalidateQueries(queryFleetMembers());
    },
  });
};

export const useSendCommandSetAutolockTimeout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.enterprisePolicies.commandSetAutolockTimeout,
    mutationFn: sendCommandSetAutolockTimeout,
    onSuccess: () => {
      void queryClient.invalidateQueries(queryFleetMembers());
    },
  });
};

export const useSendCommandSoftwareAutoInstall = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.enterprisePolicies.commandSoftwareAutoInstall,
    mutationFn: sendCommandSoftwareAutoInstall,
    onSuccess: (_, variables) => {
      toast.success(
        `Software auto-install ${variables.enable ? 'enabled' : 'disabled'}`
      );
      void queryClient.invalidateQueries(queryFleetMembers());
    },
  });
};

export const useSendCommandSoftwareForceRestart = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.enterprisePolicies.commandSoftwareForceRestart,
    mutationFn: sendCommandSoftwareForceRestart,
    onSuccess: (_, variables) => {
      toast.success(
        `Software force restart ${variables.enable ? 'enabled' : 'disabled'}`
      );
      void queryClient.invalidateQueries(queryFleetMembers());
    },
  });
};
